import { StorageKeys, StorageService } from 'src/services/storage.service';
import { UserRole } from 'src/types/enum/user-role';
import { create } from 'zustand';

export type ViewContext = 'site';

interface UserStore {
  isChangingDirectoryToken: boolean;
  setIsChangingDirectory: (isChanging: boolean) => void;

  directoryToken: string | null;
  setDirectoryToken: (token: string) => void;
  clearDirectoryToken: () => void;

  directoryTokenLockspace: string | null;
  setDirectoryTokenLockspace: (token: string) => void;
  clearDirectoryTokenLockspace: () => void;

  viewContext: ViewContext | null;
  setViewContext: (viewContext: ViewContext) => void;
  clearViewContext: () => void;

  adminToken: string | null;
  setAdminToken: (token: string) => void;
  clearAdminToken: () => void;

  role: UserRole | null;
  setRole: (role: UserRole) => void;
  clearRole: () => void;

  meilisearchToken: string | null;
  setMeilisearchToken: (token: string) => void;
  clearMeilisearchToken: () => void;

  meilisearchTokenLockspace: string | null;
  setMeilisearchTokenLockspace: (token: string) => void;
  clearMeilisearchTokenLockspace: () => void;

  isLoadingViewContextAccess: boolean;
  setIsLoadingViewContextAccess: (isLoading: boolean) => void;

  isSSO: boolean;
  setIsSSO: (isSSO: boolean) => void;
  clearIsSSO: () => void;

  mfaEnabled: boolean | null;
  setMfaEnabled: (mfaEnabled: boolean | null) => void;
  clearMfaEnabled: () => void;
}

export const useUserStore = create<UserStore>(set => ({
  isChangingDirectoryToken: false,
  setIsChangingDirectory: (isChanging: boolean) =>
    set(() => ({
      isChangingDirectoryToken: isChanging,
    })),

  directoryToken: StorageService.getItem(StorageKeys.DIRECTORY_TOKEN) ?? null,
  setDirectoryToken: directoryToken =>
    set(() => {
      StorageService.setItem(StorageKeys.DIRECTORY_TOKEN, directoryToken);

      return {
        directoryToken,
      };
    }),

  clearDirectoryToken: () => {
    StorageService.removeItem(StorageKeys.DIRECTORY_TOKEN);
    set({
      directoryToken: null,
    });
  },

  directoryTokenLockspace:
    StorageService.getItem(StorageKeys.DIRECTORY_TOKEN_LOCKSPACE) ?? null,

  setDirectoryTokenLockspace: directoryTokenLockspace =>
    set(() => {
      StorageService.setItem(
        StorageKeys.DIRECTORY_TOKEN_LOCKSPACE,
        directoryTokenLockspace,
      );

      return {
        directoryTokenLockspace,
      };
    }),
  clearDirectoryTokenLockspace: () => {
    StorageService.removeItem(StorageKeys.DIRECTORY_TOKEN_LOCKSPACE);
    set({
      directoryTokenLockspace: null,
    });
  },

  meilisearchToken: null,
  setMeilisearchToken: (meilisearchToken: string) =>
    set(() => {
      StorageService.setItem(StorageKeys.MEILISEARCH_TOKEN, meilisearchToken);

      return {
        meilisearchToken,
      };
    }),
  clearMeilisearchToken: () =>
    set(() => {
      StorageService.removeItem(StorageKeys.MEILISEARCH_TOKEN);
      return { meilisearchToken: null };
    }),

  meilisearchTokenLockspace: null,
  setMeilisearchTokenLockspace: (meilisearchTokenLockspace: string) =>
    set(() => {
      StorageService.setItem(
        StorageKeys.MEILISEARCH_TOKEN_LOCKSPACE,
        meilisearchTokenLockspace,
      );

      return {
        meilisearchTokenLockspace,
      };
    }),
  clearMeilisearchTokenLockspace: () =>
    set(() => {
      StorageService.removeItem(StorageKeys.MEILISEARCH_TOKEN_LOCKSPACE);
      return { meilisearchTokenLockspace: null };
    }),

  viewContext:
    (StorageService.getItem(StorageKeys.VIEW_CONTEXT) as ViewContext) ?? 'site',
  setViewContext: (viewContext: ViewContext) =>
    set(() => {
      StorageService.setItem(StorageKeys.VIEW_CONTEXT, viewContext);
      return { viewContext };
    }),

  clearViewContext: () =>
    set(() => {
      StorageService.removeItem(StorageKeys.VIEW_CONTEXT);
      return { viewContext: null };
    }),

  isLoadingViewContextAccess: true,
  setIsLoadingViewContextAccess: (isLoadingViewContextAccess: boolean) =>
    set(() => ({ isLoadingViewContextAccess })),

  adminToken: localStorage.getItem(StorageKeys.ADMIN_TOKEN) ?? null,

  setAdminToken: (adminToken: string) =>
    set(() => {
      StorageService.setItem(StorageKeys.ADMIN_TOKEN, adminToken);
      return {
        adminToken,
      };
    }),
  clearAdminToken: () =>
    set(() => {
      StorageService.removeItem(StorageKeys.ADMIN_TOKEN);
      return { adminToken: null };
    }),

  role: (localStorage.getItem(StorageKeys.ROLE) as UserRole) ?? null,
  setRole: (role: UserRole) =>
    set(() => {
      StorageService.setItem(StorageKeys.ROLE, role);
      return {
        role,
      };
    }),
  clearRole: () =>
    set(() => {
      StorageService.removeItem(StorageKeys.ROLE);
      return { role: null };
    }),

  isSSO: false,
  setIsSSO: (isSSO: boolean) => set(() => ({ isSSO })),
  clearIsSSO: () => set(() => ({ isSSO: false })),

  mfaEnabled: null,
  setMfaEnabled: (mfaEnabled: boolean | null) =>
    set(() => ({ mfaEnabled: mfaEnabled })),
  clearMfaEnabled: () => set(() => ({ mfaEnabled: null })),
}));

// OLD
// import { StorageKeys, StorageService } from 'src/services/storage.service';
// import { UserRole } from 'src/types/enum/user-role';
// import { create } from 'zustand';

// // export type ViewContext = 'site' | 'tenant' | 'issue-manager';
// export type ViewContext = 'site' | 'tenant' | 'issue-manager';

// //? directory = tenant | site
// interface UserStore {
//   isChangingDirectoryToken: boolean;
//   setIsChangingDirectory: (isChanging: boolean) => void;

//   directoryToken: string | null;
//   setDirectoryToken: (token: string) => void;
//   clearDirectoryToken: () => void;

//   viewContext: ViewContext | null;
//   setViewContext: (viewContext: ViewContext) => void;
//   clearViewContext: () => void;

//   adminToken: string | null;
//   setAdminToken: (token: string) => void;
//   clearAdminToken: () => void;

//   role: UserRole | null;
//   setRole: (role: UserRole) => void;
//   clearRole: () => void;

//   meilisearchToken: string | null;
//   setMeilisearchToken: (token: string) => void;
//   clearMeilisearchToken: () => void;

//   isLoadingViewContextAccess: boolean;
//   setIsLoadingViewContextAccess: (isLoading: boolean) => void;

//   isSSO: boolean;
//   setIsSSO: (isSSO: boolean) => void;
//   clearIsSSO: () => void;
// }

// export const useUserStore = create<UserStore>(set => ({
//   isChangingDirectoryToken: false,
//   setIsChangingDirectory: (isChanging: boolean) =>
//     set(() => ({
//       isChangingDirectoryToken: isChanging,
//     })),

//   directoryToken: StorageService.getItem(StorageKeys.DIRECTORY_TOKEN) ?? null,
//   setDirectoryToken: directoryToken =>
//     set(() => {
//       StorageService.setItem(StorageKeys.DIRECTORY_TOKEN, directoryToken);

//       return {
//         directoryToken,
//       };
//     }),
//   clearDirectoryToken: () =>
//     set({
//       directoryToken: null,
//     }),

//   meilisearchToken: null,
//   setMeilisearchToken: (meilisearchToken: string) =>
//     set(() => {
//       StorageService.setItem(StorageKeys.MEILISEARCH_TOKEN, meilisearchToken);

//       return {
//         meilisearchToken,
//       };
//     }),
//   clearMeilisearchToken: () =>
//     set(() => {
//       StorageService.removeItem(StorageKeys.MEILISEARCH_TOKEN);
//       return { meilisearchToken: null };
//     }),

//   viewContext:
//     (StorageService.getItem(StorageKeys.VIEW_CONTEXT) as ViewContext) ??
//     'tenant',
//   setViewContext: (viewContext: ViewContext) =>
//     set(() => {
//       StorageService.setItem(StorageKeys.VIEW_CONTEXT, viewContext);
//       return { viewContext };
//     }),

//   clearViewContext: () =>
//     set(() => {
//       StorageService.removeItem(StorageKeys.VIEW_CONTEXT);
//       return { viewContext: null };
//     }),

//   isLoadingViewContextAccess: true,
//   setIsLoadingViewContextAccess: (isLoadingViewContextAccess: boolean) =>
//     set(() => ({ isLoadingViewContextAccess })),

//   adminToken: localStorage.getItem(StorageKeys.ADMIN_TOKEN) ?? null,
//   setAdminToken: (adminToken: string) =>
//     set(() => {
//       StorageService.setItem(StorageKeys.ADMIN_TOKEN, adminToken);
//       return {
//         adminToken,
//       };
//     }),
//   clearAdminToken: () =>
//     set(() => {
//       StorageService.removeItem(StorageKeys.ADMIN_TOKEN);
//       return { adminToken: null };
//     }),

//   role: (localStorage.getItem(StorageKeys.ROLE) as UserRole) ?? null,
//   setRole: (role: UserRole) =>
//     set(() => {
//       StorageService.setItem(StorageKeys.ROLE, role);
//       return {
//         role,
//       };
//     }),
//   clearRole: () =>
//     set(() => {
//       StorageService.removeItem(StorageKeys.ROLE);
//       return { role: null };
//     }),

//   isSSO: false,
//   setIsSSO: (isSSO: boolean) => set(() => ({ isSSO })),
//   clearIsSSO: () => set(() => ({ isSSO: false })),
// }));
